/**
 * Team API client
 */

import client from './client';

export interface Team {
  id: string;
  name: string;
  ministry_id: string;
  invite_code: string;
  created_at: string;
}

export type TeamRoleCategory = 'ministerial' | 'vocal' | 'instrumental' | 'tecnica' | 'apoio';

export interface TeamRole {
  id: string;
  name: string;
  category: TeamRoleCategory;
  icon: string;
  description: string;
  is_default: boolean;
}

export interface TeamRoleInput {
  name: string;
  category: TeamRoleCategory;
  icon: string;
  description: string;
}

export interface TeamMember {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  avatar_url: string;
  role: 'ADMIN' | 'LEADER' | 'EDITOR' | 'MEMBER';
  assigned_roles: TeamRole[];
  joined_at: string;
}

/**
 * Create a new team
 */
export async function createTeam(name: string, ministryId: string): Promise<Team> {
  const response = await client.post('/teams/', { name, ministry_id: ministryId });
  return response.data;
}

/**
 * Get all teams for authenticated user
 */
export async function getTeams(): Promise<Team[]> {
  const response = await client.get('/teams/');
  return response.data;
}

/**
 * Get team details
 */
export async function getTeam(teamId: string): Promise<Team> {
  const response = await client.get(`/teams/${teamId}/`);
  return response.data;
}

/**
 * Get team members
 */
export async function getTeamMembers(teamId: string): Promise<TeamMember[]> {
  const response = await client.get(`/teams/${teamId}/members/`);
  return response.data;
}

export async function getTeamRoles(teamId: string): Promise<TeamRole[]> {
  const response = await client.get(`/teams/${teamId}/roles/`);
  return response.data;
}

export async function createTeamRole(teamId: string, role: TeamRoleInput): Promise<TeamRole> {
  const response = await client.post(`/teams/${teamId}/roles/`, role);
  return response.data;
}

export async function updateTeamRole(
  teamId: string,
  roleId: string,
  role: TeamRoleInput
): Promise<TeamRole> {
  const response = await client.put(`/teams/${teamId}/roles/${roleId}/`, role);
  return response.data;
}

export async function deleteTeamRole(teamId: string, roleId: string): Promise<void> {
  await client.delete(`/teams/${teamId}/roles/${roleId}/`);
}

export async function setMemberRoles(
  teamId: string,
  userId: string,
  roleIds: string[]
): Promise<TeamMember> {
  const response = await client.put(`/teams/${teamId}/members/${userId}/roles/`, { role_ids: roleIds });
  return response.data;
}

/**
 * Join a team via invite code
 */
export async function joinTeam(inviteCode: string): Promise<TeamMember> {
  const response = await client.post('/teams/join/', { invite_code: inviteCode });
  return response.data;
}

/**
 * Change member role (admin only)
 */
export async function changeMemberRole(
  teamId: string,
  userId: string,
  role: 'ADMIN' | 'LEADER' | 'EDITOR' | 'MEMBER'
): Promise<TeamMember> {
  const response = await client.patch(`/teams/${teamId}/members/${userId}/`, { role });
  return response.data;
}

/**
 * Remove member from team
 */
export async function removeMember(teamId: string, userId: string): Promise<void> {
  await client.delete(`/teams/${teamId}/members/${userId}/`);
}

/**
 * Delete a team (admin only)
 */
export async function deleteTeam(teamId: string): Promise<void> {
  await client.delete(`/teams/${teamId}/`);
}

export interface TeamContext {
  team_id: string;
  team_name: string;
  member_count: number;
  owner_id: string;
  owner_name: string;
  owner_plan_name: string;
  user_role: 'ADMIN' | 'LEADER' | 'EDITOR' | 'MEMBER';
  joined_at: string;
}

/**
 * Get enriched team membership info for the current user's account page
 */
export async function getUserTeamContexts(): Promise<TeamContext[]> {
  const response = await client.get('/teams/me/');
  return response.data;
}
